import { useState } from 'react'
import { useDroppable } from '@dnd-kit/react'
import { useSortable } from '@dnd-kit/react/sortable'
import { GripVertical, Trash2, ClipboardList, LayoutGrid, Lock, LockOpen, Link2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  SECTION_WIDTHS,
  SECTION_WIDTH_LABELS,
  SECTION_COLOR_HEADER_CLASS,
  sectionPrerequisiteIds,
} from '@/lib/formSections'
import { SECTION_DRAG_TYPE, acceptsAllButSections, sectionCollisionDetector, sectionGroupId } from '@/lib/formBuilderDnd'
import type { FormFieldLayout } from '@/types/cgt'
import { ATTACHMENT_SLOT_PX, contentFloorPx, gridIsSingleColumn, placeField } from '@/lib/fieldLayout'
import { browserControlMeasure } from '@/lib/controlMeasure'
import { useContainerWidth } from '@/hooks/useContainerWidth'
import { useOptionSetsByIds } from '@/hooks/useOptionSets'
import type { FormField, FormSection } from './types'
import { CanvasField } from './CanvasField'
import { WidthSkeleton, SECTION_WIDTH_COLUMNS } from './WidthSkeleton'
import { MultiSelect } from './MultiSelect'

interface Props {
  sections: FormSection[]
  /** Flat, ordered field list — threaded to each field's inline prerequisite picker. */
  allFields: FormField[]
  selectedId: string | null
  onSelectField: (id: string) => void
  onSelectSection: (id: string) => void
  onSectionChange: (section: FormSection) => void
  onDeleteField: (id: string) => void
  onDeleteSection: (id: string) => void
  onSetFieldUnlockedBy: (id: string, ids: string[]) => void
  /** Visibility click-to-pick: ids pickable as a condition source (null = not picking). */
  pickEligibleIds: Set<string> | null
  /** Called with the picked field id while pick mode is active. */
  onPickField: (id: string) => void
  /** The form's label default from Form Settings, so the canvas lays fields out as the form will. */
  fieldLayout?: FormFieldLayout | null
}

/** The field grid's gap (gap-3). */
const CANVAS_GRID_GAP_PX = 12

/**
 * What a canvas card takes around its preview: p-3 on both sides plus the
 * 1px border. Counted as trailing space, so the control floor holds for the
 * preview inside the card, not the card.
 */
const CANVAS_CARD_CHROME_PX = 26

// Section width → span in the 12-col layout grid. Literal strings so Tailwind's
// JIT compiler emits them.
const SECTION_SPAN_CLASS: Record<FormSection['width'], string> = {
  full: 'sm:col-span-12',
  half: 'sm:col-span-6',
  third: 'sm:col-span-4',
  quarter: 'sm:col-span-3',
}

/**
 * Compact, hover-revealed width picker that sits next to the section's drag
 * handle — lets authors change full/half/third/quarter on the canvas without
 * opening the Properties panel. Hover (or focus-within) opens a 2×2 grid of
 * column-layout thumbnails (full/half on top, third/quarter below).
 */
function SectionWidthControl({
  width,
  onSelect,
}: {
  width: FormSection['width']
  onSelect: (w: FormSection['width']) => void
}) {
  return (
    <div className='group/width relative shrink-0' onClick={e => e.stopPropagation()}>
      <button
        type='button'
        className='flex items-center gap-1 rounded px-1 py-0.5 text-[10px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground group-focus-within/width:bg-muted'
        aria-label='Change section width'
        aria-haspopup='menu'
      >
        <LayoutGrid className='h-3.5 w-3.5' />
        {SECTION_WIDTH_LABELS[width]}
      </button>
      {/* Flyout — kept mounted; pt-1 bridges the gap so hover doesn't drop */}
      <div className='absolute right-0 top-full z-20 hidden pt-1 group-hover/width:block group-focus-within/width:block'>
        <div className='grid w-36 grid-cols-2 gap-0.5 rounded-md border bg-popover p-1 shadow-md'>
          {SECTION_WIDTHS.map(w => (
            <button
              key={w}
              type='button'
              className={cn(
                'flex flex-col items-center gap-1 rounded border px-1.5 py-1.5 text-[11px] hover:bg-accent',
                w === width ? 'border-primary bg-accent font-medium' : 'border-transparent',
              )}
              onClick={() => onSelect(w)}
            >
              <WidthSkeleton columns={SECTION_WIDTH_COLUMNS[w]} />
              <span>{SECTION_WIDTH_LABELS[w]}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * Section-header sequencing control: a lock icon that opens an inline picker of
 * eligible EARLIER sections (reuse MultiSelect) so the author can pick this
 * section's prerequisites right at the icon. The icon reflects "has
 * prerequisites" (explicit unlockedBySections, or the legacy gatedByPrevious).
 * A non-empty explicit selection supersedes the legacy flag.
 */
function SectionLockControl({
  section,
  earlierSections,
  onSectionChange,
}: {
  section: FormSection
  earlierSections: FormSection[]
  onSectionChange: (section: FormSection) => void
}) {
  const [open, setOpen] = useState(false)
  const selected = sectionPrerequisiteIds(section)
  const hasPrereqs = selected.length > 0 || !!section.gatedByPrevious

  return (
    <div className='relative shrink-0' onClick={e => e.stopPropagation()}>
      <button
        type='button'
        className={cn(
          'rounded p-0.5',
          hasPrereqs
            ? 'text-primary hover:bg-primary/10'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
        )}
        aria-label='Edit section prerequisites'
        aria-expanded={open}
        title={
          hasPrereqs
            ? 'Locked until its prerequisite section(s) are complete'
            : 'Lock until earlier section(s) are complete'
        }
        onClick={e => {
          e.stopPropagation()
          setOpen(o => !o)
        }}
      >
        {hasPrereqs ? <Lock className='h-3.5 w-3.5' /> : <LockOpen className='h-3.5 w-3.5' />}
      </button>
      {open && (
        <>
          <button
            type='button'
            className='fixed inset-0 z-20 cursor-default'
            aria-label='Close section prerequisite picker'
            onClick={e => {
              e.stopPropagation()
              setOpen(false)
            }}
          />
          <div
            className='absolute right-0 top-full z-30 mt-1 w-64 rounded-md border bg-popover p-2 shadow-lg'
            onClick={e => e.stopPropagation()}
          >
            <p className='mb-1.5 px-0.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'>
              Unlock after other sections are complete
            </p>
            <MultiSelect
              options={earlierSections.map(s => ({
                id: s.id,
                label: s.title || 'Untitled section',
              }))}
              selected={selected}
              onChange={ids =>
                onSectionChange({
                  ...section,
                  unlockedBySections: ids.length ? ids : undefined,
                  gatedByPrevious: ids.length ? undefined : section.gatedByPrevious,
                })
              }
              placeholder='None — always unlocked'
              searchPlaceholder='Search sections…'
              emptyLabel='No sections match.'
            />
          </div>
        </>
      )}
    </div>
  )
}

interface SectionCardProps {
  section: FormSection
  index: number
  /** All sections, in order — used to offer only earlier sections as prerequisites. */
  allSections: FormSection[]
  /** Flat, ordered field list — threaded to each field's inline prerequisite picker. */
  allFields: FormField[]
  selectedId: string | null
  onSelectField: (id: string) => void
  onSelectSection: (id: string) => void
  onSectionChange: (section: FormSection) => void
  onDeleteField: (id: string) => void
  onDeleteSection: (id: string) => void
  onSetFieldUnlockedBy: (id: string, ids: string[]) => void
  pickEligibleIds: Set<string> | null
  onPickField: (id: string) => void
  fieldLayout?: FormFieldLayout | null
}

/** One section: a reorderable container that owns a droppable, stacked field list. */
function SectionCard({
  section,
  index,
  allSections,
  allFields,
  selectedId,
  onSelectField,
  onSelectSection,
  onSectionChange,
  onDeleteField,
  onDeleteSection,
  onSetFieldUnlockedBy,
  pickEligibleIds,
  onPickField,
  fieldLayout,
}: SectionCardProps) {
  // The section itself is sortable (reorder relative to other sections)…
  const { ref: sortableRef, handleRef, isDragging } = useSortable({
    id: `section:${section.id}`,
    index,
    group: 'sections',
    // Typed so nothing inside a section accepts it: a section only ever
    // reorders among sections (z8nrz7e1wt, see acceptsAllButSections).
    type: SECTION_DRAG_TYPE,
    collisionDetector: sectionCollisionDetector,
    data: { origin: 'section', sectionId: section.id },
  })
  // …and its body is a droppable that accepts fields (incl. when empty), never
  // another section.
  const groupId = sectionGroupId(section.id)
  const { ref: dropRef, isDropTarget } = useDroppable({ id: groupId, accept: acceptsAllButSections })
  // Measured so a field too narrow for its label and control widens, exactly
  // as it does on the form.
  const { ref: measureRef, width: gridPx } = useContainerWidth<HTMLDivElement>()
  // A dropdown widens for its longest option, on the canvas as on the form (z8nrz7e05f).
  const optionSetIds = [
    ...new Set(
      section.fields.flatMap(f => (f.optionsSource === 'optionSet' && f.optionSetId ? [f.optionSetId] : [])),
    ),
  ].sort()
  const optionSets = useOptionSetsByIds(optionSetIds)
  const optionLabels = (field: FormField): string[] =>
    field.optionsSource === 'optionSet' && field.optionSetId
      ? (optionSets.find(s => s.id === field.optionSetId)?.options ?? []).map(o => o.label)
      : field.options.map(o => o.label)

  const isSelected = selectedId === section.id

  return (
    <section
      ref={sortableRef}
      className={cn(
        SECTION_SPAN_CLASS[section.width],
        'rounded-xl border-2 bg-card transition-colors',
        isSelected ? 'border-primary' : 'border-border',
        isDragging && 'opacity-40',
      )}
    >
      {/* Section header — click selects the section; grip reorders it */}
      <div
        className={cn(
          'flex cursor-pointer items-center gap-1.5 rounded-t-xl border-b px-2 py-1.5',
          section.headerColor && section.headerColor !== 'none'
            ? SECTION_COLOR_HEADER_CLASS[section.headerColor]
            : 'bg-muted/40',
        )}
        onClick={() => onSelectSection(section.id)}
      >
        <button
          ref={handleRef}
          className='shrink-0 cursor-grab rounded p-0.5 text-muted-foreground hover:text-foreground active:cursor-grabbing'
          aria-label='Drag to reorder section'
          onClick={e => e.stopPropagation()}
        >
          <GripVertical className='h-3.5 w-3.5' />
        </button>
        <span
          className={cn(
            'flex-1 truncate text-xs font-semibold',
            !section.title && 'font-normal italic text-muted-foreground',
          )}
        >
          {section.title || 'Untitled section'}
        </span>
        {/* Visibility badge — this section shows/hides based on other fields */}
        {section.visibility && (
          <span
            className='flex h-5 w-5 shrink-0 items-center justify-center rounded-md border border-primary/30 bg-primary/10 text-primary'
            aria-label='Section has visibility conditions'
            title="Shown/hidden based on other fields' values"
          >
            <Link2 className='h-3 w-3' />
          </span>
        )}
        <SectionWidthControl
          width={section.width}
          onSelect={w => onSectionChange({ ...section, width: w })}
        />
        {/* Section-sequencing lock — only meaningful when a section precedes this
            one. Opens an inline picker of eligible earlier sections. */}
        {index > 0 && (
          <SectionLockControl
            section={section}
            earlierSections={allSections.slice(0, index)}
            onSectionChange={onSectionChange}
          />
        )}
        <button
          className='shrink-0 rounded p-0.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive'
          aria-label='Delete section'
          onClick={e => {
            e.stopPropagation()
            onDeleteSection(section.id)
          }}
        >
          <Trash2 className='h-3.5 w-3.5' />
        </button>
      </div>

      {/* Field list — droppable; fields flow in a 12-col grid (full=12, half=6, third=4, quarter=3) */}
      <div
        ref={node => {
          dropRef(node)
          measureRef(node)
        }}
        className={cn(
          'grid min-h-24 gap-3 p-3 transition-colors',
          gridIsSingleColumn(gridPx, CANVAS_GRID_GAP_PX) ? 'grid-cols-1' : 'grid-cols-12',
          isDropTarget && 'bg-primary/5',
        )}
      >
        {section.fields.length === 0 ? (
          <div className='col-span-12 flex h-20 flex-col items-center justify-center gap-1 text-muted-foreground'>
            <ClipboardList className='h-5 w-5 opacity-30' />
            <p className='text-[11px] opacity-70'>Drop fields here</p>
          </div>
        ) : (
          section.fields.map((field, i) => (
            <CanvasField
              key={field.id}
              field={field}
              placement={placeField(
                {
                  type: field.type,
                  requested:
                    field.type === 'table' ? 'full' : (field.fieldWidth ?? (field.colSpan === 1 ? 'half' : 'full')),
                  labelPosition: field.labelPosition,
                  labelWidth: field.labelWidth,
                  trailingPx: CANVAS_CARD_CHROME_PX + (field.attachment?.enabled ? ATTACHMENT_SLOT_PX : 0),
                  // Measured from the help text, which the form shows as the placeholder.
                  minControlPx: contentFloorPx(
                    field.type,
                    [field.helpText, ...(field.type === 'select' ? optionLabels(field) : [])],
                    browserControlMeasure,
                  ),
                },
                fieldLayout,
                gridPx,
                CANVAS_GRID_GAP_PX,
              )}
              index={i}
              group={groupId}
              sectionId={section.id}
              allFields={allFields}
              isSelected={field.id === selectedId}
              onSelect={() => onSelectField(field.id)}
              onDelete={() => onDeleteField(field.id)}
              onSetUnlockedBy={ids => onSetFieldUnlockedBy(field.id, ids)}
              pickModeActive={pickEligibleIds !== null}
              pickEligible={pickEligibleIds?.has(field.id) ?? false}
              onPick={() => onPickField(field.id)}
            />
          ))
        )}
      </div>
    </section>
  )
}

export function FormCanvas({
  sections,
  allFields,
  selectedId,
  onSelectField,
  onSelectSection,
  onSectionChange,
  onDeleteField,
  onDeleteSection,
  onSetFieldUnlockedBy,
  pickEligibleIds,
  onPickField,
  fieldLayout,
}: Props) {
  return (
    <div className='flex-1 overflow-y-auto p-6'>
      {sections.length === 0 ? (
        <div className='flex h-full min-h-72 flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-muted-foreground/20 text-muted-foreground'>
          <ClipboardList className='h-8 w-8 opacity-20' />
          <p className='text-sm font-medium'>No sections yet</p>
          <p className='text-xs opacity-70'>Add a Section from the palette to start building.</p>
        </div>
      ) : (
        <div className='grid grid-cols-1 items-start gap-4 sm:grid-cols-12'>
          {sections.map((section, i) => (
            <SectionCard
              key={section.id}
              section={section}
              index={i}
              allSections={sections}
              allFields={allFields}
              selectedId={selectedId}
              onSelectField={onSelectField}
              onSelectSection={onSelectSection}
              onSectionChange={onSectionChange}
              onDeleteField={onDeleteField}
              onDeleteSection={onDeleteSection}
              onSetFieldUnlockedBy={onSetFieldUnlockedBy}
              pickEligibleIds={pickEligibleIds}
              onPickField={onPickField}
              fieldLayout={fieldLayout}
            />
          ))}
        </div>
      )}
    </div>
  )
}
